if (!customElements.get('kit-product-builder')) {
  class KitProductBuilder extends HTMLElement {
    constructor() {
      super();
      this.selectedItems = new Map();
    }

    connectedCallback() {
      this.initSteps();
      this.initProductOptions();
      this.initSummary();
    }

    initSteps() {
      this.querySelectorAll('.kit-step__header').forEach(header => {
        header.addEventListener('click', () => {
          const step = header.closest('.kit-step');
          step.classList.toggle('is-open');
        });
      });
    }

    initProductOptions() {
      this.querySelectorAll('.kit-product-option').forEach(option => {
        const checkbox = option.querySelector('.kit-product-option__checkbox');
        const header = option.querySelector('.kit-product-option__header');
        const productId = option.dataset.productId;

        // Handle header click to toggle selection
        header.addEventListener('click', (e) => {
          // Don't toggle if clicking on the checkbox itself (it handles its own state)
          if (e.target.tagName === 'INPUT') return;
          e.preventDefault();
          checkbox.checked = !checkbox.checked;
          this.handleProductSelection(option, checkbox.checked);
        });

        // Handle checkbox change
        checkbox.addEventListener('change', (e) => {
          e.stopPropagation();
          this.handleProductSelection(option, checkbox.checked);
        });

        // Handle option dropdowns
        option.querySelectorAll('.kit-product-option__select-dropdown').forEach(select => {
          select.addEventListener('change', () => {
            this.updateVariantFromDropdowns(option);
          });
        });

        // Handle quantity controls
        const qtyInput = option.querySelector('.kit-product-option__qty-input');
        const minusBtn = option.querySelector('.kit-product-option__qty-btn--minus');
        const plusBtn = option.querySelector('.kit-product-option__qty-btn--plus');

        if (minusBtn) {
          minusBtn.addEventListener('click', () => {
            const currentVal = parseInt(qtyInput.value) || 1;
            if (currentVal > 1) {
              qtyInput.value = currentVal - 1;
              this.updateItemQuantity(productId, currentVal - 1);
            }
          });
        }

        if (plusBtn) {
          plusBtn.addEventListener('click', () => {
            const currentVal = parseInt(qtyInput.value) || 1;
            qtyInput.value = currentVal + 1;
            this.updateItemQuantity(productId, currentVal + 1);
          });
        }

        if (qtyInput) {
          qtyInput.addEventListener('change', () => {
            const val = Math.max(1, parseInt(qtyInput.value) || 1);
            qtyInput.value = val;
            this.updateItemQuantity(productId, val);
          });
        }
      });
    }

    handleProductSelection(option, isSelected) {
      const productId = option.dataset.productId;
      
      if (isSelected) {
        option.classList.add('is-selected');
        // Check if product has variant dropdowns
        const hasDropdowns = option.querySelectorAll('.kit-product-option__select-dropdown').length > 0;
        if (hasDropdowns) {
          this.updateVariantFromDropdowns(option);
        } else {
          // Handle products with only default variant (no dropdowns)
          this.addDefaultVariantProduct(option);
        }
      } else {
        option.classList.remove('is-selected');
        this.selectedItems.delete(productId);
        this.updateSummary();
      }
    }

    addDefaultVariantProduct(option) {
      const productId = option.dataset.productId;
      const variantsData = option.dataset.variants;
      
      let variantId, price, available;
      
      if (variantsData) {
        try {
          const variants = JSON.parse(variantsData);
          if (variants.length > 0) {
            variantId = variants[0].id;
            price = variants[0].price;
            available = variants[0].available;
          }
        } catch (e) {
          console.error('Error parsing variants:', e);
        }
      }
      
      // Fallback to hidden input if parsing failed
      if (!variantId) {
        const variantIdInput = option.querySelector('.kit-product-option__variant-id');
        variantId = variantIdInput ? parseInt(variantIdInput.value) : null;
        
        const priceEl = option.querySelector('.kit-product-option__price');
        price = priceEl ? this.parseMoney(priceEl.textContent) : 0;
        
        const stockEl = option.querySelector('.kit-product-option__stock');
        available = stockEl ? stockEl.classList.contains('kit-product-option__stock--in') : true;
      }
      
      if (!variantId) return;
      
      const qtyInput = option.querySelector('.kit-product-option__qty-input');
      const quantity = parseInt(qtyInput?.value) || 1;
      
      this.selectedItems.set(productId, {
        productId: productId,
        variantId: variantId,
        title: option.querySelector('.kit-product-option__title')?.textContent || '',
        variantTitle: 'Default Title',
        price: price,
        quantity: quantity,
        available: available
      });
      
      this.updateSummary();
    }

    parseMoney(moneyString) {
      // Parse money string like "£12.99" to cents (1299)
      const cleaned = moneyString.replace(/[^0-9.]/g, '');
      return Math.round(parseFloat(cleaned) * 100) || 0;
    }

    updateVariantFromDropdowns(option) {
      const productId = option.dataset.productId;
      const variantsData = option.dataset.variants;
      
      if (!variantsData) return;

      let variants;
      try {
        variants = JSON.parse(variantsData);
      } catch (e) {
        console.error('Error parsing variants:', e);
        return;
      }

      // Get selected option values
      const selectedOptions = {};
      option.querySelectorAll('.kit-product-option__select-dropdown').forEach(select => {
        const optionName = select.dataset.optionName;
        selectedOptions[optionName] = select.value;
      });

      // Find matching variant
      const matchingVariant = variants.find(variant => {
        return variant.options.every((optValue, index) => {
          const optionName = variant.optionNames[index];
          return selectedOptions[optionName] === optValue;
        });
      });

      // Use matching variant or fallback to first variant
      const selectedVariant = matchingVariant || variants[0];
      
      if (selectedVariant) {
        // Update price display
        const priceEl = option.querySelector('.kit-product-option__price');
        if (priceEl) {
          priceEl.textContent = this.formatMoney(selectedVariant.price);
        }

        // Update stock status
        const stockEl = option.querySelector('.kit-product-option__stock');
        if (stockEl) {
          if (selectedVariant.available) {
            stockEl.textContent = 'In Stock';
            stockEl.className = 'kit-product-option__stock kit-product-option__stock--in';
          } else {
            stockEl.textContent = 'Out of Stock';
            stockEl.className = 'kit-product-option__stock kit-product-option__stock--out';
          }
        }

        // Update image if variant has one
        if (selectedVariant.image) {
          const imgEl = option.querySelector('.kit-product-option__image img');
          if (imgEl) {
            imgEl.src = selectedVariant.image;
          }
        }

        // Update hidden variant ID
        const variantIdInput = option.querySelector('.kit-product-option__variant-id');
        if (variantIdInput) {
          variantIdInput.value = selectedVariant.id;
        }

        // Update selected items
        const qtyInput = option.querySelector('.kit-product-option__qty-input');
        const quantity = parseInt(qtyInput?.value) || 1;

        this.selectedItems.set(productId, {
          productId: productId,
          variantId: selectedVariant.id,
          title: option.querySelector('.kit-product-option__title')?.textContent || '',
          variantTitle: selectedVariant.title,
          price: selectedVariant.price,
          quantity: quantity,
          available: selectedVariant.available
        });

        this.updateSummary();
      }
    }

    updateItemQuantity(productId, quantity) {
      if (this.selectedItems.has(productId)) {
        const item = this.selectedItems.get(productId);
        item.quantity = quantity;
        this.updateSummary();
      }
    }

    initSummary() {
      this.summaryItems = this.querySelector('[data-kit-items]');
      this.summaryCount = this.querySelector('[data-kit-count]');
      this.summarySubtotal = this.querySelector('[data-kit-subtotal]');
      this.summaryDiscount = this.querySelector('[data-kit-discount]');
      this.summaryDiscountRow = this.querySelector('[data-kit-discount-row]');
      this.summaryTotal = this.querySelector('[data-kit-total]');
      this.addButton = this.querySelector('[data-kit-add-btn]');
      this.addButtonPrice = this.querySelector('[data-kit-btn-price]');

      if (this.addButton) {
        this.addButton.addEventListener('click', () => this.addAllToCart());
      }
    }

    updateSummary() {
      const items = Array.from(this.selectedItems.values());
      const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
      
      // Update count
      if (this.summaryCount) {
        this.summaryCount.textContent = `${totalItems} item${totalItems !== 1 ? 's' : ''} selected`;
      }

      // Update items list
      if (this.summaryItems) {
        if (items.length === 0) {
          this.summaryItems.innerHTML = '<div class="kit-summary-inline__empty">Select products from the steps above</div>';
        } else {
          this.summaryItems.innerHTML = items.map(item => `
            <div class="kit-summary-inline__item">
              <span class="kit-summary-inline__item-title">${item.title}${item.variantTitle && item.variantTitle !== 'Default Title' ? ' - ' + item.variantTitle : ''}</span>
              <span class="kit-summary-inline__item-qty">x${item.quantity}</span>
              <span class="kit-summary-inline__item-price">${this.formatMoney(item.price * item.quantity)}</span>
            </div>
          `).join('');
        }
      }

      // Calculate totals
      const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      
      // Calculate discount (5% for 3+ items, 10% for 4+ items)
      let discountPercent = 0;
      if (totalItems >= 4) {
        discountPercent = 10;
      } else if (totalItems >= 3) {
        discountPercent = 5;
      }
      
      const discountAmount = subtotal * (discountPercent / 100);
      const total = subtotal - discountAmount;

      // Update displays
      if (this.summarySubtotal) {
        this.summarySubtotal.textContent = this.formatMoney(subtotal);
      }

      if (this.summaryDiscountRow) {
        if (discountPercent > 0) {
          this.summaryDiscountRow.style.display = 'flex';
          if (this.summaryDiscount) {
            this.summaryDiscount.innerHTML = `-${this.formatMoney(discountAmount)} <span class="kit-summary-inline__discount-badge">${discountPercent}% OFF</span>`;
          }
        } else {
          this.summaryDiscountRow.style.display = 'none';
        }
      }

      if (this.summaryTotal) {
        this.summaryTotal.textContent = this.formatMoney(total);
      }

      if (this.addButtonPrice) {
        this.addButtonPrice.textContent = this.formatMoney(total);
      }

      // Enable/disable add button
      if (this.addButton) {
        const hasUnavailable = items.some(item => !item.available);
        this.addButton.disabled = items.length === 0 || hasUnavailable;
      }
    }

    formatMoney(amount) {
      return '£' + (amount / 100).toFixed(2);
    }

    async addAllToCart() {
      const items = Array.from(this.selectedItems.values());
      
      if (items.length === 0) return;

      const unavailableItems = items.filter(item => !item.available);
      if (unavailableItems.length > 0) {
        alert('Some items in your kit are out of stock. Please update your selection.');
        return;
      }

      this.addButton.disabled = true;
      const btnText = this.addButton.querySelector('.kit-summary-inline__add-btn-text');
      if (btnText) btnText.textContent = 'Adding...';

      try {
        // Get main kit product data from data attributes
        const mainVariantId = this.dataset.mainVariantId;
        const mainProductTitle = this.dataset.mainProductTitle || 'Custom Kit';
        
        // Generate a unique kit ID for grouping
        const kitId = 'kit-' + Date.now();
        
        // Build properties object with all selected items info
        const properties = {
          '_kit_id': kitId,
          '_kit_bundle': 'true'
        };
        
        // Add each selected item as a property
        items.forEach((item, index) => {
          const itemNum = index + 1;
          const variantInfo = item.variantTitle && item.variantTitle !== 'Default Title' 
            ? ` (${item.variantTitle})` 
            : '';
          properties[`Kit Item ${itemNum}`] = `${item.title}${variantInfo} x${item.quantity}`;
        });
        
        // Calculate total kit price (sum of all selected items)
        const totalPrice = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        properties['_kit_total'] = this.formatMoney(totalPrice);
        
        // Option C: Add individual products with kit identification properties
        // Each product will have properties showing it's part of a kit order
        // This allows tracking kit orders while having accurate pricing
        const cartItems = [];
        
        items.forEach((item, index) => {
          cartItems.push({
            id: parseInt(item.variantId),
            quantity: item.quantity,
            properties: {
              'Kit Order': mainProductTitle,
              'Kit ID': kitId,
              'Kit Item': `${index + 1} of ${items.length}`
            }
          });
        });

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items: cartItems })
        });

        if (!response.ok) {
          throw new Error('Failed to add items to cart');
        }

        if (btnText) btnText.textContent = 'Added!';
        
        if (typeof window.refreshCart === 'function') {
          window.refreshCart();
        }

        setTimeout(() => {
          window.location.href = '/cart';
        }, 500);

      } catch (error) {
        console.error('Error adding kit to cart:', error);
        if (btnText) btnText.textContent = 'Error - Try Again';
        this.addButton.disabled = false;
        
        setTimeout(() => {
          if (btnText) btnText.textContent = 'Add Kit to Cart';
        }, 2000);
      }
    }
  }

  customElements.define('kit-product-builder', KitProductBuilder);
}
